import React, { useState } from "react";
import { BadgePercent, Loader2, X } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@pgs-core/ui/components/card";
import { Button } from "@pgs-core/ui/components/button";
import { Input } from "@pgs-core/ui/components/input";
import { Label } from "@pgs-core/ui/components/label";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@pgs-core/ui/components/select";
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from "@pgs-core/ui/components/table";
import type { AdjustmentDuration, AdjustmentType, BillingAdjustment, ForecastLine } from "@pgs-core/types";
import {
  adjustmentDurationLabel,
  adjustmentTargetLabel,
  adjustmentValueLabel,
} from "@pgs-core/portal-lib/billing";
import type { ApiClient } from "../lib/api";
import { cancelRecurringAdjustment, createRecurringAdjustment } from "../lib/billing";

interface BillingAdjustmentsCardProps {
  api: ApiClient | null;
  clientId: string;
  adjustments: BillingAdjustment[];
  /** The next charge's lines — the line items an adjustment can target. */
  lines: ForecastLine[];
  onChanged: (notice: string) => void;
  onError: (message: string) => void;
}

/**
 * Recurring line-item adjustments: one active per line item, applied pre-VAT
 * from the next billing date, floored at £0.00. Cancelling keeps history.
 */
export function BillingAdjustmentsCard({
  api, clientId, adjustments, lines, onChanged, onError,
}: BillingAdjustmentsCardProps) {
  const [working, setWorking] = useState(false);
  const [target, setTarget] = useState("plan");
  const [type, setType] = useState<AdjustmentType>("percent");
  const [value, setValue] = useState("");
  const [duration, setDuration] = useState<AdjustmentDuration>("indefinite");
  const [durationMonths, setDurationMonths] = useState("3");
  const [endsOn, setEndsOn] = useState("");

  const run = async (notice: string, action: () => Promise<unknown>) => {
    if (!api || working) return;
    setWorking(true);
    try {
      await action();
      onChanged(notice);
    } catch (err) {
      onError((err as Error).message);
    } finally {
      setWorking(false);
    }
  };

  const numericValue = parseFloat(value);
  const valueValid =
    type === "percent"
      ? numericValue > 0 && numericValue <= 100
      : type === "fixed"
        ? numericValue > 0
        : numericValue >= 0;
  const durationValid =
    duration === "indefinite" ||
    (duration === "months" ? parseInt(durationMonths, 10) >= 1 : Boolean(endsOn));

  const submit = () =>
    run("Adjustment created — it applies from the next billing date.", () =>
      createRecurringAdjustment(api!, clientId, {
        line_kind: target === "plan" ? "plan" : "module",
        ...(target === "plan" ? {} : { module_slug: target }),
        adjustment_type: type,
        ...(type === "percent"
          ? { value_bps: Math.round(numericValue * 100) }
          : { value_pence: Math.round(numericValue * 100) }),
        duration_type: duration,
        ...(duration === "months" ? { duration_months: parseInt(durationMonths, 10) } : {}),
        ...(duration === "until_date" ? { ends_on: endsOn } : {}),
      }).then(() => setValue(""))
    );

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0 pb-2">
        <div>
          <CardTitle className="text-base">Recurring adjustments</CardTitle>
          <CardDescription>
            One active adjustment per line item. Applied before VAT from the next
            billing date; line totals never go below £0.00.
          </CardDescription>
        </div>
        <BadgePercent className="h-4 w-4 shrink-0 text-muted-foreground" />
      </CardHeader>
      <CardContent className="space-y-4">
        {adjustments.length === 0 ? (
          <p className="text-sm text-muted-foreground">No active adjustments.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Line item</TableHead>
                <TableHead>Adjustment</TableHead>
                <TableHead>Starts</TableHead>
                <TableHead>Duration</TableHead>
                <TableHead className="text-right">Cancel</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {adjustments.map((adj) => (
                <TableRow key={adj.id}>
                  <TableCell>{adjustmentTargetLabel(adj)}</TableCell>
                  <TableCell>{adjustmentValueLabel(adj)}</TableCell>
                  <TableCell>{adj.starts_on}</TableCell>
                  <TableCell>{adjustmentDurationLabel(adj)}</TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={working || !api}
                      onClick={() => run("Adjustment cancelled.", () => cancelRecurringAdjustment(api!, clientId, adj.id))}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        <div className="flex flex-wrap items-end gap-3 border-t pt-4">
          <div className="space-y-1.5">
            <Label>Line item</Label>
            <Select value={target} onValueChange={setTarget}>
              <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
              <SelectContent>
                {lines.map((line) => (
                  <SelectItem key={`${line.kind}-${line.slug}`} value={line.kind === "plan" ? "plan" : line.slug}>
                    {line.description}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Type</Label>
            <Select value={type} onValueChange={(v: string) => setType(v as AdjustmentType)}>
              <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="percent">Percentage discount</SelectItem>
                <SelectItem value="fixed">Fixed discount (£)</SelectItem>
                <SelectItem value="override">Price override (£)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="adj-value">{type === "percent" ? "Percent off" : "Amount (£)"}</Label>
            <Input
              id="adj-value"
              className="w-28"
              inputMode="decimal"
              placeholder={type === "percent" ? "10" : "5.00"}
              value={value}
              onChange={(e) => setValue(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Duration</Label>
            <Select value={duration} onValueChange={(v: string) => setDuration(v as AdjustmentDuration)}>
              <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="indefinite">Indefinite</SelectItem>
                <SelectItem value="months">Next X months</SelectItem>
                <SelectItem value="until_date">Until a date</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {duration === "months" && (
            <div className="space-y-1.5">
              <Label htmlFor="adj-months">Months</Label>
              <Input
                id="adj-months"
                className="w-20"
                inputMode="numeric"
                value={durationMonths}
                onChange={(e) => setDurationMonths(e.target.value)}
              />
            </div>
          )}
          {duration === "until_date" && (
            <div className="space-y-1.5">
              <Label htmlFor="adj-ends">End date</Label>
              <Input id="adj-ends" type="date" className="w-40" value={endsOn} onChange={(e) => setEndsOn(e.target.value)} />
            </div>
          )}
          <Button size="sm" disabled={working || !api || !valueValid || !durationValid} onClick={submit}>
            {working ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Add adjustment
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
